import AuditLogsUseCase from "./AuditLogsUseCase";
import FindObjectUseCase from "../object/FindObjectUseCase";
import { getAllSchemasUseCase } from "../schema/usecases";
import { EXCLUDED_AUDIT_COLLECTIONS } from "./auditLogConstants";

const COMMON_LABEL_FIELDS = [
  "name",
  "title",
  "username",
  "email",
  "code",
  "agency",
  "contributionType",
  "description",
  "label",
  "reference_no",
  "referenceNo",
  "position_title",
  "jobTitle",
  "Firstname",
  "surname",
];

export { EXCLUDED_AUDIT_COLLECTIONS };

let cachedSchemas = null;
let schemasLoadPromise = null;

function loadSchemas() {
  if (cachedSchemas) return Promise.resolve(cachedSchemas);
  if (!schemasLoadPromise) {
    schemasLoadPromise = getAllSchemasUseCase()
      .execute()
      .then((schemas) => {
        cachedSchemas = schemas?.length ? schemas : [];
        return cachedSchemas;
      })
      .catch((err) => {
        schemasLoadPromise = null;
        throw err;
      });
  }
  return schemasLoadPromise;
}

async function getSchema(collection) {
  const schemas = await loadSchemas();
  return schemas.find((s) => s.collection === collection) || null;
}

function getPointerIncludes(fields, object) {
  if (!fields || !object) return [];
  const includes = [];
  for (const key of Object.keys(object)) {
    if (fields[key]?.type === "Pointer") includes.push(key);
  }
  return includes;
}

function getLabelKeys(fields) {
  if (!fields) return COMMON_LABEL_FIELDS;
  const indexed = Object.entries(fields)
    .filter(([, def]) => def?._index || def?.index)
    .sort(([, a], [, b]) => (a.index ?? a._index ?? 0) - (b.index ?? b._index ?? 0))
    .map(([key]) => key);
  const common = COMMON_LABEL_FIELDS.filter((key) => fields[key]);
  return [...new Set([...indexed, ...common])];
}

function snapshotForAudit(value) {
  if (value == null || typeof value !== "object") return value;
  try {
    return structuredClone(value);
  } catch {
    return JSON.parse(JSON.stringify(value));
  }
}

function shouldSkipAudit(collection) {
  return EXCLUDED_AUDIT_COLLECTIONS.has(collection);
}

function formatCollectionLabel(collection) {
  return collection.replace(/_/g, " ").toUpperCase();
}

async function runAudit(collection, object, activity, action, options = {}) {
  if (shouldSkipAudit(collection)) return;
  await new AuditLogsUseCase().execute(collection, object, activity, action, options);
}

async function loadPreviousData(collection, object, fields) {
  if (!object?.id) return null;

  const pointerIncludes = getPointerIncludes(fields, object);
  const labelKeys = getLabelKeys(fields);
  const payloadKeys = Object.keys(object).filter(
    (key) => key !== "acl" && (!fields || fields[key]),
  );
  const keys = [...new Set([...labelKeys, ...payloadKeys, ...pointerIncludes])];
  const query = {
    where: { id: object.id },
    limit: 1,
    ...(keys.length ? { keys } : {}),
    ...(pointerIncludes.length ? { include: pointerIncludes } : {}),
  };

  try {
    const existing = await new FindObjectUseCase().execute(collection, query);
    return existing?.[0] ?? null;
  } catch (e) {
    console.error(e.message || e);
    try {
      const existing = await new FindObjectUseCase().execute(collection, {
        where: { id: object.id },
        limit: 1,
      });
      return existing?.[0] ?? null;
    } catch (retryError) {
      console.error(retryError.message || retryError);
      return null;
    }
  }
}

/**
 * User saves replace role objects with `{ id, name: id }`, and the main
 * previous-record query does not include the roles relation. Load roles
 * separately so a user-role update can name the roles before and after.
 * Failure leaves the rest of the audit record unchanged.
 */
async function attachPreviousUserRoles(previousData, object) {
  if (
    !previousData?.id ||
    !Object.prototype.hasOwnProperty.call(object, "roles")
  ) {
    return previousData;
  }

  try {
    const rows = await new FindObjectUseCase().execute("users", {
      where: { id: object.id },
      limit: 1,
      keys: ["roles"],
      include: ["roles"],
    });
    if (rows?.[0] && Object.prototype.hasOwnProperty.call(rows[0], "roles")) {
      previousData.roles = rows[0].roles;
    }
  } catch (e) {
    console.error(e.message || e);
  }

  return previousData;
}

/**
 * Record an audit log for create/update before persisting via UpsertUseCase.
 */
export async function recordUpsertAudit(collection, object) {
  if (shouldSkipAudit(collection) || !object) return;

  const formattedCollection = formatCollectionLabel(collection);
  const schema = await getSchema(collection);
  const fields = schema?.fields;
  const payload = snapshotForAudit(object);
  let previousData = await loadPreviousData(collection, object, fields);
  if (collection === "users" && previousData) {
    previousData = await attachPreviousUserRoles(previousData, object);
  }

  if (previousData) {
    await runAudit(
      collection,
      payload,
      `UPDATE ${formattedCollection}`,
      "UPDATE",
      { previousData, schema },
    );
    return;
  }

  await runAudit(collection, payload, `ADD ${formattedCollection}`, "ADD", {
    schema,
  });
}

/**
 * Record an audit log for delete before soft-delete via DeleteObjectUseCase.
 */
export async function recordDeleteAudit(collection, id) {
  if (shouldSkipAudit(collection) || id == null) return;

  const formattedCollection = formatCollectionLabel(collection);
  const schema = await getSchema(collection);
  const pointerIncludes = schema?.fields
    ? Object.keys(schema.fields).filter(
        (key) => schema.fields[key]?.type === "Pointer",
      )
    : [];
  const labelKeys = getLabelKeys(schema?.fields);

  const objects = await new FindObjectUseCase().execute(collection, {
    where: { id },
    ...(labelKeys.length ? { keys: labelKeys } : {}),
    ...(pointerIncludes.length ? { include: pointerIncludes } : {}),
  });
  const object = objects[0] || { id };

  await runAudit(
    collection,
    object,
    `DELETE ${formattedCollection}`,
    "DELETE",
    { schema },
  );
}

/**
 * Record an audit log for sign-out.
 */
export async function recordSignOutAudit() {
  await runAudit("OUT", {}, "Successful Logout", "LOGGED");
}
